import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./editor.module.css";
import { editorService } from "./editorService";
import { normalizeUrl, suggestedName, type DetectedLink } from "./sharepointLinks";

import { Button } from "@/components/Button";

interface SourceDocumentsProps {
  pageId: string;
  csrfToken: string;
  /** SharePoint links found in the page content (see EditorPage). Any that
   * aren't registered yet are offered as one-click "add as source" suggestions. */
  contentLinks?: DetectedLink[];
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Page-level SharePoint source links — the editing side of the "Source
 * documents on SharePoint" block readers see on the published manual page
 * (ManualPage). Links belong to the page, not the draft version, so this box
 * stays available whether or not a draft is currently in review.
 */
export function SourceDocuments({ pageId, csrfToken, contentLinks = [] }: SourceDocumentsProps) {
  const [links, setLinks] = useState<{ id: string; name: string; webUrl: string }[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The control that opened the add dialog, so focus returns to it on close.
  const openerRef = useRef<HTMLElement | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    editorService
      .getSharepointLinks(pageId)
      .then((res) => {
        if (cancelled) return;
        setLinks(res.links);
        setLoadError(false);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  useEffect(() => load(), [load]);

  const addLink = async () => {
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName) {
      setFormError("Enter a name for the document.");
      return;
    }
    if (!isHttpsUrl(trimmedUrl)) {
      setFormError("Enter a valid https:// SharePoint link.");
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      await editorService.addSharepointLink(pageId, trimmedUrl, trimmedName, csrfToken);
      setName("");
      setUrl("");
      setAddOpen(false);
      load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Couldn't add that link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const removeLink = async (linkId: string) => {
    setBusy(true);
    try {
      await editorService.removeSharepointLink(linkId, csrfToken);
      load();
    } catch {
      setLoadError(true);
    } finally {
      setBusy(false);
    }
  };

  // Pre-fill the add form from a detected link rather than adding silently, so
  // the editor confirms the name and sees the audience warning before it lands.
  const promote = (link: DetectedLink) => {
    setName(suggestedName(link));
    setUrl(link.href);
    setFormError(null);
    setAddOpen(true);
  };

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    setFormError(null);
    openerRef.current?.focus();
  }, []);

  // Escape closes the dialog (capture, so it wins over editor shortcuts).
  useEffect(() => {
    if (!addOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeAdd();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [addOpen, closeAdd]);

  // Links present in the content but not yet registered as source documents.
  const registered = new Set((links ?? []).map((link) => normalizeUrl(link.webUrl)));
  const suggestions = contentLinks.filter((link) => !registered.has(normalizeUrl(link.href)));

  return (
    <div className={styles.sourceBox}>
      <div className={styles.sourceHeader}>
        <h3 className={styles.sourceBoxTitle}>Source documents</h3>
        <span className={styles.sourceHint}>(Source documents are readable by AI)</span>
      </div>

      {loadError && (
        <p className={styles.sourceError}>Couldn&rsquo;t load source documents. Try again.</p>
      )}

      {links === null && !loadError && <p className={styles.sourceLoading}>Loading…</p>}

      {links && (links.length > 0 || suggestions.length > 0) && (
        <ul className={styles.sourceList}>
          {links.map((link) => (
            <li key={link.id} className={styles.sourceItem}>
              <a href={link.webUrl} target="_blank" rel="noopener noreferrer">
                {link.name}
              </a>
              <button
                type="button"
                className={styles.sourceRemove}
                onClick={() => void removeLink(link.id)}
                disabled={busy}
                aria-label={`Remove ${link.name}`}
              >
                Remove
              </button>
            </li>
          ))}
          {suggestions.map((link) => (
            <li key={link.href} className={styles.sourceItem}>
              <span className={styles.sourceName} title={link.href}>
                {suggestedName(link)}
              </span>
              <button
                type="button"
                className={styles.sourceAdd}
                onClick={(e) => {
                  openerRef.current = e.currentTarget;
                  promote(link);
                }}
                disabled={busy}
              >
                Add as source
              </button>
            </li>
          ))}
        </ul>
      )}

      {links && links.length === 0 && suggestions.length === 0 && (
        <p className={styles.sourceEmpty}>No documents linked yet.</p>
      )}

      <div className={styles.sourceAddRow}>
        <button
          type="button"
          className={styles.sourceAddLink}
          onClick={(e) => {
            openerRef.current = e.currentTarget;
            setAddOpen(true);
          }}
        >
          + Link a document
        </button>
      </div>

      {addOpen &&
        createPortal(
          <div
            className={styles.modalOverlay}
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) closeAdd();
            }}
          >
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="source-doc-dialog-title"
            >
              <h4 id="source-doc-dialog-title" className={styles.modalTitle}>
                Link a document
              </h4>
              <label className={styles.inlineLabel} htmlFor="source-doc-name">
                Document name
              </label>
              <input
                id="source-doc-name"
                type="text"
                value={name}
                placeholder="e.g. Site safety plan"
                className={styles.inlineInput}
                autoFocus
                onChange={(e) => setName(e.target.value)}
              />
              <label className={styles.inlineLabel} htmlFor="source-doc-url">
                SharePoint URL
              </label>
              <input
                id="source-doc-url"
                type="url"
                value={url}
                placeholder="https://…"
                className={styles.inlineInput}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void addLink();
                }}
              />
              {formError && <p className={styles.sourceError}>{formError}</p>}
              <p className={styles.sourceGuidance}>
                Becomes answerable to anyone who can see this page. Link restricted documents only
                from restricted sections.
              </p>
              <div className={styles.inlineActions}>
                <Button variant="primary" onClick={() => void addLink()} disabled={busy}>
                  Add document
                </Button>
                <Button variant="secondary" onClick={closeAdd}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
